import { useEffect, useState } from "react";
import {
	Badge,
	Box,
	BoxProps,
	Heading,
	HStack,
	Image,
	Link,
	Spinner,
	Stack,
	Text,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { useCmsApi } from "../../services/useCmsApi";
import { IArticle } from "../../models/IArticle";

interface INewsItem {
	type: string;
	typeColor: string;
	title: string;
	source?: string;
	sourceLogoUrl?: string;
	date?: string;
	links: { label: string; href: string }[];
}

const fallbackItems: INewsItem[] = [
	{
		type: "Video",
		typeColor: "red",
		title: "Geco Predictive Weed Control",
		source: "YouTube",
		date: "2-minute animated explainer",
		links: [
			{ label: "Watch on YouTube", href: "https://www.youtube.com/watch?v=ZA4NQV8vVng" },
		],
	},
	{
		type: "News",
		typeColor: "green",
		title: "Gowan and Geco Partner to Bring Strategic Weed Management to Canadian Growers",
		source: "Gowan Canada",
		links: [
			{ label: "Read Article", href: "https://ca.gowanco.com/news/gowan-and-geco-partner-bring-strategic-weed-management-canadian-growers" },
		],
	},
	{
		type: "Podcast",
		typeColor: "purple",
		title: "Predictive Weed Control with Greg Stewart of Geco Strategic Weed Management",
		source: "Future of Agriculture Podcast",
		date: "November 2024",
		links: [
			{ label: "Web", href: "https://futureofagriculture.com/episode/predictive-weed-control-with-greg-stewart-of-geco-strategic-weed-management" },
			{ label: "Spotify", href: "https://open.spotify.com/episode/2Ubt4SVOldHuKDO5JBohzA?si=fqAAfcy8SjO-ImYQnVvf4A" },
			{ label: "Apple Podcasts", href: "https://podcasts.apple.com/us/podcast/predictive-weed-control-with-greg-stewart-of/id1137767458?i=1000676774755" },
			{ label: "YouTube", href: "https://youtu.be/VygI606X554?si=rFxjq0yYKReDG3uB" },
		],
	},
	{
		type: "Article",
		typeColor: "blue",
		title: "Predictive maps help weed control",
		source: "The Western Producer",
		date: "December 2023",
		links: [
			{ label: "Read Article", href: "https://www.producer.com/crops/predictive-maps-help-weed-control/" },
		],
	},
	{
		type: "Article",
		typeColor: "blue",
		title: "High-tech weed control to give farmers a look into how precise spraying can be",
		source: "Grainews",
		date: "July 2023",
		links: [
			{ label: "Read Article", href: "https://www.grainews.ca/features/high-tech-weed-control-to-give-farmers-a-look-into-how-precise-spraying-can-be/" },
		],
	},
];

const mapArticleToNewsItem = (a: IArticle): INewsItem => ({
	type: a.type || "Article",
	typeColor: a.typeColor || "blue",
	title: a.title,
	source: a.source,
	sourceLogoUrl: a.sourceLogoUrl,
	date: a.description,
	links: a.links?.length
		? a.links.map((l) => ({ label: l.label, href: l.url }))
		: [{ label: "Read Article", href: a.url }],
});


export const NewsList = ({ ...rest }: BoxProps): JSX.Element => {
	const cmsApi = useCmsApi();
	const [newsItems, setNewsItems] = useState<INewsItem[]>(fallbackItems);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		cmsApi.getArticles(20).then((articles) => {
			if (!cancelled && articles.length > 0) {
				setNewsItems(articles.map(mapArticleToNewsItem));
			}
			setLoading(false);
		});
		return () => { cancelled = true; };
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Box {...rest}>
			<Heading size={{ base: "md", md: "xl" }} color="gray.800" mb="8">
				Recent Articles &amp; Announcements
			</Heading>
			{loading && <Spinner color="green.500" />}
			<Stack spacing="4">
				{newsItems.map((item) => (
					<Box
						key={item.title}
						bg="white"
						border="1px solid"
						borderColor="gray.200"
						borderRadius="xl"
						p={{ base: "5", md: "6" }}
						boxShadow="sm"
					>
						<Stack spacing="3">
							<HStack spacing="3" flexWrap="wrap">
								<Badge colorScheme={item.typeColor} fontSize="xs" px="2" py="0.5" borderRadius="md">
									{item.type}
								</Badge>
								{item.source && (
									item.sourceLogoUrl ? (
										<Image src={item.sourceLogoUrl} alt={item.source} h="20px" maxW="80px" objectFit="contain" title={item.source} />
									) : (
										<Text fontSize="sm" color="gray.500" fontWeight="medium">
											{item.source}
										</Text>
									)
								)}
								{item.date && (
									<Text fontSize="sm" color="gray.400">
										{item.date}
									</Text>
								)}
							</HStack>
							<Heading size="sm" color="gray.800" lineHeight="1.4">
								{item.title}
							</Heading>
							<HStack spacing="4" flexWrap="wrap">
								{item.links.map((link) => (
									<Link
										key={link.label}
										href={link.href}
										isExternal
										color="blue.600"
										fontSize="sm"
										fontWeight="medium"
										_hover={{ color: "blue.800", textDecoration: "underline" }}
									>
										{link.label} <ExternalLinkIcon mx="2px" />
									</Link>
								))}
							</HStack>
						</Stack>
					</Box>
				))}
			</Stack>
		</Box>
	);
};
